import { useEffect, useRef, useState } from "react";

import { isPresent, isNotPresent } from "neetocist";
import { Info } from "neetoicons";
import { Input } from "neetoui/formik";
import PropTypes from "prop-types";
import { isEmpty } from "ramda";

import CopyToClipboardButton from "components/CopyToClipboardButton";

import { INPUT_FIELD_MIN_WIDTH } from "./constants";
import "./suffixed-input.scss";

const SuffixedInput = ({
  placeholder,
  helpUrl,
  helpIconTooltipContent,
  label,
  value,
  getInputRef,
  suffix,
  isCopyToClipboardEnabled = false,
  name = "email",
  inputProps = {},
}) => {
  const [initialInputWidth, setInitialInputWidth] = useState(
    INPUT_FIELD_MIN_WIDTH
  );
  const inputRef = useRef(null);
  const placeholderRef = useRef(null);

  const setInputRef = ref => {
    inputRef.current = ref;
    getInputRef?.(ref);
  };

  useEffect(() => {
    //To handle the input width on initial render.
    const placeholderSpanWidth = placeholderRef.current?.offsetWidth;
    setInitialInputWidth(placeholderSpanWidth);
  }, []);

  return (
    <>
      <span // Dummy element to calculate the width to be set for the input. Do not remove.
        className="pointer-events-none invisible absolute ps-2"
        ref={placeholderRef}
      >
        {value || placeholder}
      </span>
      <div className="inline-flex max-w-full items-end gap-2">
        <Input
          {...{ label, name, placeholder, ...inputProps }}
          className="nm-suffixed-input-field"
          ref={setInputRef}
          type="text"
          labelProps={{
            helpIconProps: isPresent(helpUrl) && {
              icon: Info,
              className:
                "cursor-pointer opacity-75 hover:opacity-100 transition-opacity duration-300 ease-in-out",
              onClick: () => window.open(helpUrl, "_blank"),
              tooltipProps: {
                content: helpIconTooltipContent,
                position: "right",
                disabled: isNotPresent(helpUrl),
              },
            },
          }}
          style={{
            minWidth: isEmpty(value) ? INPUT_FIELD_MIN_WIDTH : 0,
            width: placeholderRef.current?.offsetWidth ?? initialInputWidth, // Additional 2px to act as a buffer.
          }}
          suffix={
            // Added onClick to focus the input even when the suffix part is clicked.
            <span onClick={() => inputRef.current?.focus()}>{suffix}</span>
          }
        />
        {isCopyToClipboardEnabled && (
          <CopyToClipboardButton
            disabled={isEmpty(value)}
            style="text"
            value={`${value}${suffix}`}
          />
        )}
      </div>
    </>
  );
};

SuffixedInput.propTypes = {
  /**
   * Placeholder of the input field.
   */
  placeholder: PropTypes.string,
  /**
   * A help icon will be displayed next to the label if helpUrl is present. On clicking the icon will take the user to the URL.
   */
  helpUrl: PropTypes.string,
  /**
   * Text to be displayed inside the tooltip of the help icon.
   */
  helpIconTooltipContent: PropTypes.string,
  /**
   * Label of the input field.
   */
  label: PropTypes.string,
  /**
   * The value entered by the user, eg: email.
   */
  value: PropTypes.string,
  /**
   * A method to get the ref of the input field.
   */
  getInputRef: PropTypes.func,
  /**
   * Domain name to be rendered next to the input field.
   */
  suffix: PropTypes.string,
  /**
   * Whether to show the copy to clipboard button.
   */
  isCopyToClipboardEnabled: PropTypes.bool,
  /**
   * Formik field name of the input field.
   */
  name: PropTypes.string,
  /**
   * This will be passed down to the `Input` component.
   */
  inputProps: PropTypes.object,
};

export default SuffixedInput;
